import { useState, useEffect, useCallback, Component, type ReactNode } from 'react';
import {
  Plus, Trash2, KeyRound, Loader2, Copy, Check,
  RefreshCw, Pencil, X, ShieldCheck, Users as UsersIcon, TrendingUp,
} from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-8">
          <p className="text-sm text-red-500">Something went wrong loading this view.</p>
          <button onClick={() => this.setState({ error: null })}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-500">
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface OrgMember {
  id: string;
  username: string;
  role: 'admin' | 'manager' | 'trader';
  orgId: string;
  createdAt: string;
}

interface Org {
  id: string;
  name: string;
  slug: string;
}

interface TraderDetail {
  balance: unknown;
  positions: unknown;
  orders: unknown;
  fills: unknown;
}

interface DetailResult {
  ok: boolean;
  status: number | null;
  data: unknown;
  error: string | null;
}

interface ManagerDashboardProps {
  currentView: string;
  adminMode?: boolean;
}

export function ManagerDashboard({ currentView, adminMode }: ManagerDashboardProps) {
  return (
    <ErrorBoundary>
      <div className="h-full overflow-auto p-6">
        <TradersPanel adminMode={adminMode} />
      </div>
    </ErrorBoundary>
  );
}

// ── Role Badge ──────────────────────────────────────────────────

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  manager: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  trader: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
};

const ROLE_ICONS: Record<string, typeof ShieldCheck> = {
  admin: ShieldCheck,
  manager: UsersIcon,
  trader: TrendingUp,
};

function RoleBadge({ role }: { role: string }) {
  const Icon = ROLE_ICONS[role] ?? TrendingUp;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_STYLES[role] ?? ''}`}>
      <Icon size={10} />
      {role}
    </span>
  );
}

// ── Traders Panel ───────────────────────────────────────────────

function TradersPanel({ adminMode }: { adminMode?: boolean }) {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [resetTarget, setResetTarget] = useState<OrgMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgMember | null>(null);

  useEffect(() => {
    if (!adminMode) return;
    (async () => {
      const res = await apiFetch('/api/admin/orgs');
      const data = await res.json();
      const orgList: Org[] = data.orgs ?? [];
      setOrgs(orgList);
      if (orgList.length > 0 && !selectedOrgId) {
        setSelectedOrgId(orgList[0].id);
      }
      setLoading(false);
    })();
  }, [adminMode]);

  const orgQueryParam = adminMode ? `?orgId=${selectedOrgId}` : '';

  const fetchMembers = useCallback(async () => {
    if (adminMode && !selectedOrgId) { setLoading(false); return; }
    setLoading(true);
    const res = await apiFetch(`/api/manager/traders${orgQueryParam}`);
    const data = await res.json();
    setMembers(data.traders ?? []);
    setLoading(false);
  }, [orgQueryParam, adminMode, selectedOrgId]);

  useEffect(() => {
    if (adminMode && !selectedOrgId) return;
    fetchMembers();
  }, [fetchMembers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    if (adminMode && !selectedOrgId) return;
    setSubmitting(true); setError(null); setGeneratedPassword(null);
    const res = await apiFetch(`/api/manager/traders${orgQueryParam}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); setSubmitting(false); return; }
    setMembers((prev) => [...prev, data.user]);
    setGeneratedPassword(data.generatedPassword);
    setUsername(''); setShowForm(false); setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await apiFetch(`/api/manager/traders/${deleteTarget.id}${orgQueryParam}`, { method: 'DELETE' });
    if (res.ok) {
      setMembers((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    } else { const data = await res.json(); setError(data.error); }
    setDeleteTarget(null);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setGeneratedPassword(null);
    const res = await apiFetch(`/api/manager/traders/${resetTarget.id}/reset-password${orgQueryParam}`, { method: 'POST' });
    const data = await res.json();
    if (res.ok) setGeneratedPassword(data.generatedPassword);
    else setError(data.error);
    setResetTarget(null);
  };

  const handleEditSave = async (id: string) => {
    setError(null);
    const res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: editUsername.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
    setEditingId(null);
  };

  const isTrader = (m: OrgMember) => m.role === 'trader';

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Traders</h2>
        <button onClick={() => { setShowForm(!showForm); setGeneratedPassword(null); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> Create Trader
        </button>
      </div>

      {adminMode && (orgs.length > 0 ? (
        <div className="mb-4">
          <label className="text-muted-foreground mb-1.5 block text-xs font-medium">Organization</label>
          <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}
            className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none">
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      ) : !loading ? (
        <div className="bg-muted/50 border-border mb-4 rounded-lg border p-6 text-center">
          <p className="text-muted-foreground text-sm">No organizations exist yet.</p>
          <p className="text-muted-foreground mt-1 text-xs">Create one in the Admin panel to manage traders.</p>
        </div>
      ) : null)}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="trader1" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {submitting && <Loader2 size={12} className="animate-spin" />} Create
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      <PasswordBanner password={generatedPassword} onDismiss={() => setGeneratedPassword(null)} />
      {error && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="text-muted-foreground h-5 w-5 animate-spin" /></div>
      ) : members.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">No members yet.</p>
      ) : (
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              editing={editingId === member.id}
              editUsername={editUsername}
              onStartEdit={() => { setEditingId(member.id); setEditUsername(member.username); }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={() => handleEditSave(member.id)}
              onEditUsernameChange={setEditUsername}
              onResetPassword={isTrader(member) ? () => setResetTarget(member) : undefined}
              onDelete={isTrader(member) ? () => setDeleteTarget(member) : undefined}
              orgQueryParam={orgQueryParam}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Generate a new password for "${resetTarget?.username}"? The current password will be invalidated immediately.`}
        confirmLabel="Reset Password"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Trader"
        message={`Are you sure you want to delete "${deleteTarget?.username}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </section>
  );
}

// ── Member Card ─────────────────────────────────────────────────

interface MemberCardProps {
  member: OrgMember;
  editing: boolean;
  editUsername: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditUsernameChange: (v: string) => void;
  onResetPassword?: () => void;
  onDelete?: () => void;
  orgQueryParam: string;
}

function MemberCard(props: MemberCardProps) {
  const {
    member, editing, editUsername,
    onStartEdit, onCancelEdit, onSaveEdit, onEditUsernameChange,
    onResetPassword, onDelete, orgQueryParam,
  } = props;

  return (
    <div className="bg-card border-border flex flex-col rounded-lg border">
      <div className="p-3">
        <div className="mb-1.5 flex items-start justify-between">
          {editing ? (
            <input value={editUsername} onChange={(e) => onEditUsernameChange(e.target.value)}
              className="bg-background border-border text-foreground rounded border px-2 py-0.5 text-sm font-medium focus:border-blue-500 focus:outline-none" />
          ) : (
            <span className="text-foreground text-sm font-medium">{member.username}</span>
          )}
          <RoleBadge role={member.role} />
        </div>
        <p className="text-muted-foreground text-[11px]">Joined {new Date(member.createdAt).toLocaleDateString()}</p>
      </div>

      <div className="border-border flex items-center justify-end border-t px-3 py-1.5">
        <div className="flex items-center gap-0.5">
          {editing ? (
            <>
              <button onClick={onSaveEdit} className="rounded p-1 text-green-500 hover:bg-green-500/10"><Check size={14} /></button>
              <button onClick={onCancelEdit} className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><X size={14} /></button>
            </>
          ) : (
            <>
              {member.role === 'trader' && (
                <button onClick={onStartEdit} title="Edit" className="text-muted-foreground rounded p-1 hover:bg-blue-500/10 hover:text-blue-400"><Pencil size={12} /></button>
              )}
              {onResetPassword && (
                <button onClick={onResetPassword} title="Reset password" className="text-muted-foreground rounded p-1 hover:bg-yellow-500/10 hover:text-yellow-400"><KeyRound size={12} /></button>
              )}
              {onDelete && (
                <button onClick={onDelete} title="Delete" className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={12} /></button>
              )}
            </>
          )}
        </div>
      </div>

      <TraderDetailPanel traderId={member.id} orgQueryParam={orgQueryParam} />
    </div>
  );
}

// ── Trader Detail (always visible) ──────────────────────────────

function TraderDetailPanel({ traderId, orgQueryParam = '' }: { traderId: string; orgQueryParam?: string }) {
  const [data, setData] = useState<TraderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const safeFetch = async (url: string): Promise<DetailResult> => {
        try {
          const res = await apiFetch(url);
          const data = await res.json().catch(() => null);
          const errorMessage = (
            data
            && typeof data === 'object'
            && 'error' in data
            && typeof data.error === 'string'
          )
            ? data.error
            : null;
          return {
            ok: res.ok,
            status: res.status,
            data,
            error: errorMessage,
          };
        } catch {
          return { ok: false, status: null, data: null, error: 'Network error' };
        }
      };

      const [balRes, posRes, ordRes, fillRes] = await Promise.all([
        safeFetch(`/api/manager/traders/${traderId}/balance${orgQueryParam}`),
        safeFetch(`/api/manager/traders/${traderId}/positions${orgQueryParam}`),
        safeFetch(`/api/manager/traders/${traderId}/orders${orgQueryParam}`),
        safeFetch(`/api/manager/traders/${traderId}/fills${orgQueryParam}`),
      ]);

      const balance = balRes.ok ? balRes.data : null;
      const positions = posRes.ok ? posRes.data : null;
      const orders = ordRes.ok ? ordRes.data : null;
      const fills = fillRes.ok ? fillRes.data : null;

      const detailResults = [balRes, posRes, ordRes, fillRes];
      const anySuccess = detailResults.some((result) => result.ok);
      const firstError = detailResults.find((result) => !result.ok && result.error)?.error ?? null;

      if (!anySuccess) {
        if (firstError?.includes('No credentials stored for this user')) {
          setError('No credentials stored for this member.');
        } else if (firstError?.includes('Member not found in this org')) {
          setError('You do not have access to this member\'s trading data.');
        } else if (firstError?.startsWith('Kalshi API')) {
          setError('Kalshi request failed for this member.');
        } else if (firstError === 'Network error') {
          setError('Could not reach the server while loading this member.');
        } else {
          setError(firstError ?? 'Failed to fetch data');
        }
      } else if (detailResults.some((result) => !result.ok)) {
        setError('Some trading data could not be loaded for this member.');
      }

      setData({ balance, positions, orders, fills });
    } catch {
      setError('Failed to fetch data');
    }
    setLoading(false);
  }, [traderId, orgQueryParam]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) {
    return (
      <div className="border-border flex justify-center border-t py-4">
        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="border-border border-t px-3 py-3">
        <p className="text-muted-foreground text-xs">{error}</p>
      </div>
    );
  }

  const balanceData = data?.balance as Record<string, unknown> | null;
  const posData = data?.positions as Record<string, unknown> | null;
  const ordData = data?.orders as Record<string, unknown> | null;
  const fillData = data?.fills as Record<string, unknown> | null;

  const toArr = (v: unknown): unknown[] => Array.isArray(v) ? v : [];
  const positionsArr = posData
    ? toArr(posData.market_positions ?? posData.positions ?? posData.results)
    : [];
  const ordersArr = ordData
    ? toArr(ordData.orders ?? ordData.results)
    : [];
  const fillsArr = fillData
    ? toArr(fillData.fills ?? fillData.results)
    : [];

  return (
    <div className="border-border space-y-3 border-t px-3 py-3">
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-[11px]">
          Balance:{' '}
          <span className="text-foreground font-medium">
            {balanceData
              ? `$${(Number(balanceData.balance ?? balanceData.available_balance ?? 0) / 100).toFixed(2)}`
              : 'N/A'}
          </span>
        </div>
        <button onClick={fetchDetail} title="Refresh"
          className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]">
          <RefreshCw size={10} /> Refresh
        </button>
      </div>

      {error && <p className="text-[11px] text-yellow-500">{error}</p>}

      <DataSection title="Positions" count={positionsArr.length}>
        {positionsArr.length === 0 ? (
          <p className="text-muted-foreground text-[11px]">No open positions.</p>
        ) : (
          <table className="w-full text-[11px]">
            <thead><tr className="text-muted-foreground">
              <th className="pb-0.5 text-left font-medium">Ticker</th>
              <th className="pb-0.5 text-left font-medium">Side</th>
              <th className="pb-0.5 text-right font-medium">Qty</th>
            </tr></thead>
            <tbody>
              {positionsArr.slice(0, 10).map((p: any, i) => (
                <tr key={i} className="text-foreground">
                  <td className="py-0.5 font-mono">{p.ticker ?? p.market_ticker ?? '—'}</td>
                  <td className="py-0.5">{(p.position ?? 0) >= 0 ? 'YES' : 'NO'}</td>
                  <td className="py-0.5 text-right">{Math.abs(p.position ?? p.quantity ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataSection>

      <DataSection title="Open Orders" count={ordersArr.length}>
        {ordersArr.length === 0 ? (
          <p className="text-muted-foreground text-[11px]">No open orders.</p>
        ) : (
          <table className="w-full text-[11px]">
            <thead><tr className="text-muted-foreground">
              <th className="pb-0.5 text-left font-medium">Ticker</th>
              <th className="pb-0.5 text-left font-medium">Side</th>
              <th className="pb-0.5 text-right font-medium">Qty</th>
            </tr></thead>
            <tbody>
              {ordersArr.slice(0, 10).map((o: any, i) => (
                <tr key={i} className="text-foreground">
                  <td className="py-0.5 font-mono">{o.ticker ?? o.market_ticker ?? '—'}</td>
                  <td className="py-0.5">{o.side ?? '—'}</td>
                  <td className="py-0.5 text-right">{o.remaining_count ?? o.count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataSection>

      <DataSection title="Recent Fills" count={fillsArr.length}>
        {fillsArr.length === 0 ? (
          <p className="text-muted-foreground text-[11px]">No recent fills.</p>
        ) : (
          <table className="w-full text-[11px]">
            <thead><tr className="text-muted-foreground">
              <th className="pb-0.5 text-left font-medium">Ticker</th>
              <th className="pb-0.5 text-left font-medium">Side</th>
              <th className="pb-0.5 text-right font-medium">Count</th>
            </tr></thead>
            <tbody>
              {fillsArr.slice(0, 10).map((f: any, i) => (
                <tr key={i} className="text-foreground">
                  <td className="py-0.5 font-mono">{f.ticker ?? f.market_ticker ?? '—'}</td>
                  <td className="py-0.5">{f.side ?? '—'}</td>
                  <td className="py-0.5 text-right">{f.count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataSection>
    </div>
  );
}

function DataSection({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-muted-foreground mb-1 text-[11px] font-medium">
        {title} <span className="opacity-50">({count})</span>
      </h4>
      {children}
    </div>
  );
}

// ── Shared ──────────────────────────────────────────────────────

function PasswordBanner({
  password,
  onDismiss,
}: {
  password: string | null;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!password) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 rounded-lg border border-green-700/50 bg-green-900/20 p-4 dark:border-green-800">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-green-600 dark:text-green-400">Generated Password</span>
        <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
      </div>
      <div className="flex items-center gap-2">
        <code className="bg-card border-border flex-1 rounded border px-3 py-1.5 font-mono text-sm text-green-700 dark:text-green-300">
          {password}
        </code>
        <button onClick={handleCopy} title="Copy" className="text-muted-foreground hover:text-foreground hover:bg-muted rounded p-1.5">
          {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
      </div>
      <p className="text-muted-foreground mt-2 text-xs">Save this password now. It will not be shown again.</p>
    </div>
  );
}
